import { useQuery } from '@tanstack/react-query';
import { useAdmin } from '../adminContext';
import { crudApi } from '../crud';
import { lookupPath, recordLabel, type FieldDef } from '../resources';

export interface ReferenceOption {
  value: string;
  label: string;
}

/**
 * The ids a reference may take, when its field is limited by another field's value (`FieldDef.limitTo`): undefined while
 * that field is empty or the list loads.
 */
export function useAllowedValues(limit: FieldDef['limitTo'], values: Readonly<Record<string, unknown>> | undefined) {
  const { client } = useAdmin();
  const key = limit && values?.[limit.field] ? String(values[limit.field]) : undefined;
  return useQuery({
    queryKey: ['reference-limit', limit?.path, key],
    enabled: Boolean(limit && key),
    staleTime: 30_000,
    queryFn: async (): Promise<Set<string>> => {
      if (!limit || !key) return new Set();
      const page = await crudApi<Record<string, unknown> & { id: string; version: number }, never>(
        client,
        limit.path.replace('{id}', key),
      ).list({ pageSize: 500 });
      return new Set(
        page.items
          .filter((row) => !limit.activeField || row[limit.activeField] !== false)
          .map((row) => String(row[limit.valueField])),
      );
    },
  });
}

/** How many records of a referenced resource are loaded up front; a larger resource is searched on the server instead. */
export const FIRST_PAGE = 500;

export interface ReferencePage {
  options: ReferenceOption[];
  /** Every record of the resource is in `options`; otherwise pickers search on the server. */
  complete: boolean;
}

type Row = Record<string, unknown> & { id: string; version: number };

function useReferenceResource(resourceKey: string | undefined) {
  const { resources, client } = useAdmin();
  const def = resourceKey ? resources[resourceKey] : undefined;
  const path = def ? lookupPath(def) : null;
  const toOption = (record: Row) => ({
    value: String(record[def?.valueField ?? 'id']),
    label: def ? recordLabel(def, record) : String(record.id),
  });
  return { def, path, api: path ? crudApi<Row, never>(client, path) : undefined, toOption };
}

/** The first records of a referenced resource, and whether that is all of them. Shared with `useReferenceOptions`. */
export function useReferencePage(resourceKey: string | undefined) {
  const { def, path, api, toOption } = useReferenceResource(resourceKey);
  return useQuery({
    queryKey: ['reference', path],
    enabled: Boolean(def && api),
    staleTime: 60_000,
    queryFn: async (): Promise<ReferencePage> => {
      if (!api) return { options: [], complete: true };
      const page = await api.list({ pageSize: FIRST_PAGE });
      return { options: page.items.map(toOption), complete: page.items.length >= Number(page.total) };
    },
  });
}

/**
 * The first records of the referenced resource as picker options (all of them for the usual master data). For a resource
 * larger than `FIRST_PAGE`, a picker should use `ReferenceComboBox`, which searches on the server.
 */
export function useReferenceOptions(resourceKey: string | undefined) {
  const page = useReferencePage(resourceKey);
  return { ...page, data: page.data?.options };
}

/**
 * How many records a picker loads when it opens (ADR 0099). A resource with no more than this is loaded whole and filtered as
 * the user types; a larger one (items, categories, stores by the thousand) shows these first and is searched on the server.
 */
export const PICKER_FIRST_PAGE = 100;

/** How many matches a picker shows for what the user typed; typing more narrows them. */
export const PICKER_MATCHES = 50;

/**
 * A picker's first records, and whether that is all of them. Asked with `count=false`: the server returns one record more than
 * asked when there are more, and never counts them all (a scan on a large table).
 */
export function useReferencePickerPage(resourceKey: string | undefined) {
  const { def, path, api, toOption } = useReferenceResource(resourceKey);
  return useQuery({
    queryKey: ['reference-picker', path],
    enabled: Boolean(def && api),
    staleTime: 60_000,
    queryFn: async (): Promise<ReferencePage> => {
      if (!api) return { options: [], complete: true };
      const page = await api.list({ pageSize: PICKER_FIRST_PAGE, count: 'false' });
      return { options: page.items.map(toOption), complete: Number(page.total) <= PICKER_FIRST_PAGE };
    },
  });
}

/**
 * Records matching `text`, searched on the server by part of the code or name (a trigram index, ADR 0099), at most
 * `PICKER_MATCHES`, never counted; for a resource too large to load up front.
 */
export function useReferenceSearch(resourceKey: string | undefined, text: string, enabled: boolean) {
  const { path, api, toOption } = useReferenceResource(resourceKey);
  return useQuery({
    queryKey: ['reference-search', path, text],
    enabled: enabled && Boolean(api) && text.trim().length > 0,
    staleTime: 30_000,
    placeholderData: (previous) => previous,
    queryFn: async (): Promise<{ options: ReferenceOption[]; more: boolean }> => {
      if (!api) return { options: [], more: false };
      const page = await api.list({ search: text.trim(), pageSize: PICKER_MATCHES, count: 'false' });
      return { options: page.items.map(toOption), more: Number(page.total) > PICKER_MATCHES };
    },
  });
}

/**
 * One referenced record by id, for a value that isn't among the first records of a large resource: its label for a picker's
 * selection or a list cell. Resources keyed by something else than `id` are small and always loaded whole.
 */
export function useReferenceRecord(
  resourceKey: string | undefined,
  value: string | null | undefined,
  enabled: boolean,
) {
  const { def, path, api, toOption } = useReferenceResource(resourceKey);
  return useQuery({
    queryKey: ['reference-record', path, value],
    enabled: enabled && Boolean(api && value) && !def?.valueField,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<ReferenceOption | null> => (api && value ? toOption(await api.get(value)) : null),
  });
}
